import React, { useEffect, useRef, useState } from "react";
import "./PlatformSoon.css";
import serverRoomBg from "../../images/3d-rendering-server-room-server-computers.jpg";

/* ─────────────────────────────────────────────────────────────
   Small helpers (UX guidelines 3.3 – 3.5)
   ───────────────────────────────────────────────────────────── */

/* Entrance animation: fade + short upward slide, once per section.
   Skipped entirely when the visitor prefers reduced motion. */
const Reveal = ({ children, className = "" }) => {
  const ref = useRef(null);
  const [state, setState] = useState("idle"); // idle → armed → shown

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") {
      setState("shown");
      return undefined;
    }
    setState("armed");
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setState("shown");
          io.disconnect(); // trigger once — never replays on scroll-back
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -6% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`ec-reveal ec-reveal-${state} ${className}`}>
      {children}
    </div>
  );
};

/* Section wrapper — tone alternates white / tint to mark each new "chapter". */
const Section = ({ tone, id, children }) => (
  <section className={`ec-section ec-tone-${tone}`} id={id}>
    <Reveal>
      <div className="ec-container">{children}</div>
    </Reveal>
  </section>
);

/* Numbered eyebrow + H2 + lead paragraph. */
const SectionHead = ({ n, label, title, sub }) => (
  <header className="ec-head">
   {/*  <div className="ec-eyebrow">{n} — {label}</div> */}
     <div className="ec-eyebrow">{label}</div>
    <h2 className="ec-h2">{title}</h2>
    {sub && <p className="ec-lead">{sub}</p>}
  </header>
);

/* 12-column grid: choose a column span that keeps rows balanced. */
const spanOf = (count, i, override) => {
  if (override) return override;
  if (count === 5) return i < 3 ? 4 : 6;
  if (count === 2) return 6;
  if (count === 4) return 3;
  return 4;
};

/* Chain of steps joined by arrows (works horizontally on desktop, stacks on mobile). */
const Chain = ({ steps, numbered = false }) => (
  <div className="ec-chain">
    {steps.map((s, i) => (
      <React.Fragment key={i}>
        <div className="ec-chain-step">
          {numbered && <span className="ec-step-num">{i + 1}</span>}
          {s.icon && <div className="ec-card-icon" aria-hidden="true">{s.icon}</div>}
          <h3 className="ec-h3">{s.label}</h3>
          <p>{s.desc}</p>
        </div>
        {i < steps.length - 1 && <div className="ec-arrow" aria-hidden="true">→</div>}
      </React.Fragment>
    ))}
  </div>
);

/* Inline chain of short words joined by arrows. */
const WordChain = ({ words }) => (
  <div className="ec-wordchain">
    {words.map((w, i) => (
      <React.Fragment key={i}>
        <span className="ec-chip ec-chip-strong">{w}</span>
        {i < words.length - 1 && <span className="ec-arrow-inline" aria-hidden="true">→</span>}
      </React.Fragment>
    ))}
  </div>
);

const PlatformSoon = () => {

  /* ── What GBR Farming Provides ── */
  const provisionFlow = [
    { icon: "", label: "Ecosystem Orchestration Platform (EOP)", desc: "EOP is the platform that enables organisations to build, orchestrate, operate, govern, and scale their ecosystem networks." },
    { icon: "", label: "ENO — Ecosystem Network Operator", desc: "Builds · Owns · Operates · Governs . Sacles · Monetizes an ecosystem network." },
    { icon: "", label: "Ecosystem Network", desc: "Connects participants, organisations, operations, data, assets,digital tools and intelligence into one governed ecosystem." },
  ];

  /* ── Ecosystem Orchestration Platform (EOP) capabilities ── */
  const eopCapabilities = [
    { icon: "", title: "Ecosystem Network Creation" },
    { icon: "", title: "Participant Orchestration" },
    { icon: "", title: "Micro-Ecosystem Management" },
    { icon: "", title: "Workflow & Process Orchestration" },
    { icon: "", title: "Network Operations" },
    { icon: "", title: "Governance" },
    { icon: "", title: "Roles, Access & Permissions" },
    { icon: "", title: "Policy & Rules" },
    { icon: "", title: "Network Lifecycle Management" },
  ];

  /* ── Ecosystem Network Construction (ABN Framework) ── */
  const constructionFlow = [
    { icon: "", label: "ABN Framework", desc: "Provides the models, guidelines and building blocks for constructing ecosystem networks." },
    { icon: "", label: "Network App Types", desc: "Reusable and composable ecosystem capabilities." },
    { icon: "", label: "Network Models", desc: "Configurable blueprints defining participants, relationships, workflows, services and governance." },
    { icon: "", label: "Network Instances", desc: "Deployed ecosystem networks operated by the ENO." },
    { icon: "", label: "Your Ecosystem Network", desc: "Your actual operating ecosystem." },
  ];

  /* ── Active Ecosystem Networks ── */
  const activeNetworks = [
    { code: "BEN", name: "Bee Economy Network" },
    { code: "FEN", name: "Farm Enterprise Network" },
    { code: "CMN", name: "Contract Manufacturing Network" },
    { code: "ADN", name: "AgroDealer Network" },
    { code: "CBON", name: "Community Based Organization Network" },
    { code: "SCN", name: "Sustainability & Climate Network" },
  ];

  /* ── Micro-Ecosystem Networks ── */
  const microNetworks = [
    { code: "AGN", name: "Agri Asset Governance Network" },
    { code: "CBN", name: "Community Based Network" },
    { code: "PPN", name: "Plant Production Network" },
    { code: "AMN", name: "Artisan Manager Network" },
    { code: "FPN", name: "Field Production Network" },
    { code: "BPN", name: "BioChar Production Network" },
  ];

  /* ── Ecosystem Packages ── */
  const packages = [
    { tier: "Free", desc: "Foundational access so every participant can onboard into the network." },
    { tier: "Basic", desc: "Core digital tools for everyday participation." },
    { tier: "Standard", desc: "The full operational toolset for active participants.", highlight: true },
    { tier: "Preferred", desc: "Extended capabilities, intelligence and network services." },
    { tier: "Premium", desc: "The complete digital tools, intelligence and services stack." },
  ];

  const packageComponents = ["Digital Tools", "Features", "Access Levels", "Usage Limits", "Intelligence", "Network Capabilities", "Services"];

  /* ── Ecosystem Digital Tools ── */
  const digitalToolCategories = [
    { icon: "", title: "Participant & Discovery", items: ["Onboarding", "Profiles", "Directory", "Discovery"] },
    { icon: "", title: "Commerce & Transactions", items: ["Marketplace", "Procurement", "Orders", "Billing & Payments"] },
    { icon: "", title: "Operations", items: ["Agent App", "Workflow Management", "Inventory", "Shipment & Delivery"] },
    { icon: "", title: "Ecosystem Management", items: ["Micro-Ecosystem Network", "Participant Management", "Network Operations"] },
    { icon: "", title: "Traceability & Monitoring", items: ["Traceability", "IoT/IoB Monitoring", "Alerts", "Asset Tracking"] },
    { icon: "", title: "Data & Intelligence", items: ["Analytics", "Dashboards", "AI Tools", "Decision Support"] },
  ];

  /* ── Ecosystem Intelligence ── */
  const intelligenceLayers = [
    { icon: "", title: "Executive Intelligence", desc: "Ecosystem performance · KPIs · Trends · Forecasts · Decision Support" },
    { icon: "", title: "Site Intelligence", desc: "Farm · Factory · Warehouse · Processing · Distribution" },
    { icon: "", title: "Asset Intelligence", desc: "Equipment · Machinery · Connected Assets · Asset Performance" },
    { icon: "", title: "Ecosystem Intelligence", desc: "Network Health · Participant Activity · Supply & Demand · Opportunities · Risks" },
    { icon: "", title: "AI-Native Capabilities", desc: "AI Assistants · Agentic Workflows · Decision Support · Intelligent Operations" },
  ];

  /* ── Ecosystem Infrastructure stacks ── */
  const infrastructureStacks = [
    { icon: "", title: "Sovereign Ecosystem Data Infrastructure", items: ["Sovereign Data Hub", "MDC", "IoT / IoB Data Pipelines", "Edge Computing", "Data Processing", "Ecosystem Edge AI", "Analytics", "Event Processing"] },
    { icon: "", title: "Business Operations Infrastructure", items: ["Exchange Infrastructure", "Business & Contract Docs", "Logistic & Storage","Work Order Management", "GenDoc", "AI Ecosystem Builder"] },
    { icon: "", title: "Device & Asset Infrastructure", items: ["Device Onboarding", "Device & Asset Identity", "Device Provisioning", "Device Configuration", "OTA / FoTA", "Device & Asset Lifecycle", "Asset Onboarding", "Device Security"] },
    { icon: "", title: "Agentic Network OS", items: ["Ecosystem IAM", "Ecosystem Network Management", "Ecosystem Governance", "Communication Infrastructue", " Data Infrastructure", "3rd Party Integartion"] },
  ];

  /* ── Sovereign & Hybrid Architecture ── */
  const sovereignModel = [
    { icon: "", title: "Shared", desc: "Common EOP services and ecosystem capabilities provide scale and efficiency." },
    { icon: "", title: "Dedicated", desc: "Each ENO can have a dedicated Sovereign Data Hub (SDH) for its ecosystem data and intelligence." },
    { icon: "", title: "Distributed", desc: "MDC / edge infrastructure brings computing, data processing and AI closer to physical operations." },
  ];

  const deploymentOptions = ["MDC / eMDC", "Private Cloud", "Customer Data Centre", "Approved Sovereign Infrastructure"];

  /* ── Micro-Ecosystem Infrastructure ── */
  const microEcosystemAssets = ["Sites", "Assets", "Users", "Teams", "Local Operators", "Field Operations", "Site Intelligence", "Asset Intelligence"];

  /* ── Integration & Interoperability ── */
  const integrationFlow = [
    { icon: "", label: "Connect", desc: "ERP · CRM · MES · WMS · TMS · IoT · Devices · Machinery · Enterprise Systems" },
    { icon: "", label: "Integrate", desc: "APIs · SDKs · Data Pipelines · Event Streams · Protocols" },
    { icon: "", label: "Orchestrate", desc: "ANOS · EOP · SDH · Ecosystem Networks" },
    { icon: "", label: "Operate", desc: "Workflows · Services · Transactions · Intelligence · AI" },
  ];

  /* ── Security, Trust & Governance ── */
  const governanceCapabilities = [
    { icon: "", title: "Identity & Identity Federation", items: ["Organisation & Participant Identity", "Enterprise & Multi-Tenant IAM", "Role & Permission Management", "Device & Agent Identity"] },
    { icon: "", title: "Trust & Security", items: ["Policy & Rules", "Audit & Traceability"] },
    { icon: "", title: "Data Governance", items: ["Data Sovereignty"] },
  ];

  /* ── Infrastructure Differentiation ── */
  const differentiators = [
    { icon: "", vs: "Hosted SaaS", title: "Sovereign, not hosted", desc: "Your ecosystem operates on sovereign infrastructure, giving you control over its data, infrastructure and governance." },
    { icon: "", vs: "ERP Systems", title: "Agentic, not record-keeping", desc: "ERPs record what happened. ANOS orchestrates what happens next — coordinating intelligent workflows, agents and decisions across the ecosystem." },
    { icon: "", vs: "Marketplaces", title: "Infrastructure, not intermediary", desc: "A marketplace connects buyers and sellers. GBR provides the infrastructure for organisations to build, own and operate their own ecosystem commerce." },
    { icon: "", vs: "Point Solutions", title: "Integrated, not fragmented", desc: "Instead of stitching together point solutions, GBR brings identity, intelligence, governance, data and operations into one coordinated ecosystem infrastructure." },
    { icon: "", vs: "Cloud-Only Platforms", title: "Edge-first, not cloud-only", desc: "GBR extends compute, AI inference and data sovereignty to the operational edge through eMDC — closer to farms, facilities, clusters and rural operations." },
    { icon: "", vs: "Traditional Enterprise Systems", title: "Network-native, not siloed", desc: "GBR is built around ecosystems — multiple organisations and participants operating on a shared, sovereign foundation." },
  ];

  const differenceTable = [
    ["Application-centric", "Ecosystem-centric"],
    ["Organisation-focused", "Multi-participant"],
    ["User management", "Participant orchestration"],
    ["Application workflows", "Ecosystem workflows"],
    ["Application data", "Ecosystem data"],
    ["Cloud software", "Digital + edge + physical infrastructure"],
    ["Analytics", "Ecosystem intelligence"],
    ["Centralised operations", "Distributed operations"],
    ["Individual applications", "Composable ecosystem networks"],
  ];

  const maturitySteps = [
    { icon: "", label: "Software", desc: "Runs applications" },
    { icon: "", label: "Platform", desc: "Connects applications and users" },
    { icon: "", label: "Ecosystem Infrastructure", desc: "Connects and orchestrates organisations, participants, applications, data, intelligence and physical assets" },
  ];

  /* ── Operating Model ── */
  const gbrProvides = ["ANOS", "EOP", "ABN Framework", "Ecosystem Infrastructure", "Ecosystem Packages", "Digital Tools", "Ecosystem Intelligence", "SDH / MDC", "Connectivity & Device Infrastructure"];
  const enoOwns = ["Ecosystem", "Ecosystem Network", "Participants", "Network Model", "Business Model", "Services", "Operations", "Governance", "Growth"];

  /* ── From Business to Ecosystem ── */
  const traditionalBusiness = ["Organisation", "Customers", "Transactions"];
  const ecosystemBusiness = ["ENO", "Participants", "Micro-Ecosystems", "Services", "Assets", "Data", "Intelligence", "Transactions"];
  return (
    <div className="ec-page">

      {/* ── Hero ── */}
      <section className="ec-hero" style={{ backgroundImage: `url(${serverRoomBg})` }}>
        <div className="ec-hero-overlay" />
        <Reveal>
          <div className="ec-container ec-hero-inner">
            <div className="ec-hero-badge">AI-NATIVE ECOSYSTEM ORCHESTRATION PLATFORM</div>
            <h1 className="ec-h1">
              Ecosystem Infrastructure <span className="ec-accent-text">to Build, Operate, Govern and Scale</span>
            </h1>
            <p className="ec-hero-sub">
              Build, operate, govern and scale your own ecosystem with sovereign infrastructure that orchestrates participants, organisations, digital tools, data, AI, devices and assets.
            </p>
            <ul className="ec-hero-pillars">
              {["Build", "Operate", "Govern", "Scale"].map((w, i) => (
                <li key={i} className="ec-hero-pillar">
                  <span aria-hidden="true">{["🏗️", "⚙️", "🏛️", "📈"][i]}</span> {w}
                </li>
              ))}
            </ul>
            {/* <a href="/book-demo" className="ec-btn ec-btn-light">Request a Demo →</a> */}
           <a href="/book-demo" className="ec-btn ec-btn-light ec-hero-demo-btn">
                   Request a Demo
          </a>
            
          </div>
        </Reveal>
      </section>

      {/* ── 01 What GBR Farming Provides ── */}
     {/*  <Section tone="tint">
        <SectionHead
          n="01" label="What GBR Farming Provides"
          title={<>Infrastructure for <span className="ec-accent-text">Ecosystem Operations</span></>}
          sub="GBR Farming is not simply a software platform. It provides the ecosystem infrastructure required for an organisation to become an Ecosystem Network Operator (ENO) and build and operate its own Ecosystem Networks."
        />
        <div className="">
          {provisionFlow.map((s, i) => (
            <React.Fragment key={i}>
              <div className="ec-flow-step">
                <div className="ec-card-icon" aria-hidden="true">{s.icon}</div>
                <h3 className="ec-h3">{s.label}</h3>
                <p>{s.desc}</p>
              </div>
              {i < provisionFlow.length - 1 && <div className="ec-arrow-down" aria-hidden="true">↓</div>}
            </React.Fragment>
          ))}
        </div>
      </Section> */}
      <Section tone="tint">
<SectionHead
    n="01"
    label="What GBR Farming Provides"
    title={
      <>
        Infrastructure for{" "}
        <span className="ec-accent-text">Ecosystem Operations</span>
      </>
    }
    sub="GBR Farming is not simply an enterprise business application or software platform. It provides the ecosystem infrastructure required for an organisation to become an Ecosystem Network Operator (ENO) and build and operate its own Ecosystem Networks"
    />

   <div className="ec-grid">
    {provisionFlow.map((s, i) => (
      <div
        key={i}
        className={`ec-card ec-span-${spanOf(
          provisionFlow.length,
          i
        )}`}
      >
        <div className="ec-card-icon" aria-hidden="true">
          {s.icon}
        </div>

        <h3 className="ec-h3">
          {s.label}
        </h3>

        <p>
          {s.desc}
        </p>
      </div>
    ))}
  </div>
</Section>

      {/* ── 02 Ecosystem Orchestration ── */}
      <Section tone="white">
        <SectionHead
          n="02" label="Ecosystem Orchestration"
          title={<>Build and Orchestrate <span className="ec-accent-text">Your Ecosystem Network</span></>}
          sub="The Ecosystem Orchestration Platform (EOP) enables an ENO to create, provision, orchestrate, govern, operate and scale ecosystem networks — the operational layer that turns ecosystem infrastructure into an operating environment for the ENO."
        />
        <div className="ec-grid">
          {eopCapabilities.map((c, i) => (
            <div key={i} className={`ec-card ec-iconlabel ec-span-${spanOf(eopCapabilities.length, i)}`}>
              <span className="ec-iconlabel-icon" aria-hidden="true">{c.icon}</span>
              <span className="ec-iconlabel-text">{c.title}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 03 Ecosystem Network Construction ── */}
      <Section tone="tint">
        <SectionHead
          n="03" label="Ecosystem Network Construction"
          title={<>Construct Your <span className="ec-accent-text">Ecosystem Network</span></>}
          sub="GBR Farming enables ENOs to construct configurable ecosystem networks using the ABN Framework (Agentic Business Network)."
        />
        <div className="ec-flow">
          {constructionFlow.map((s, i) => (
            <React.Fragment key={i}>
              <div className="ec-flow-step">
                <span className="ec-step-num">{i + 1}</span>
                <h3 className="ec-h3">{s.icon} {s.label}</h3>
                <p>{s.desc}</p>
              </div>
              {i < constructionFlow.length - 1 && <div className="ec-arrow-down" aria-hidden="true">↓</div>}
            </React.Fragment>
          ))}
        </div>

       {/*  <h3 className="ec-subhead">Active Ecosystem Networks Operating Today</h3>
        <div className="ec-grid">
          {activeNetworks.map((n, i) => (
            <div key={i} className={`ec-card ec-code-card ec-span-${spanOf(activeNetworks.length, i)}`}>
              <span className="ec-code-badge">{n.code}</span>
              <span className="ec-code-name">{n.name}</span>
            </div>
          ))}
        </div> */}
      </Section>

      {/* ── 04 Ecosystem Packages ── */}
      <Section tone="white">
        <SectionHead
          n="04" label="Ecosystem Packages"
          title={<>Flexible Packages for <span className="ec-accent-text">Every Ecosystem Network Participant</span></>}
          sub="Ecosystem Packages enable an ENO to provide different levels of ecosystem capabilities to participants based on their needs, participation level and network requirements — defined, configured and evolved through the Ecosystem Orchestration Platform."
        />
        <div className="ec-ladder">
          {packages.map((p, i) => (
            <div
              key={i}
              className={`ec-card ec-tier ${p.highlight ? "ec-tier-hl" : ""}`}
              style={{ "--lvl": i + 1 }}
            >
              <div className="ec-tier-bar" aria-hidden="true"><span style={{ width: `${(i + 1) * 20}%` }} /></div>
              <div className="ec-eyebrow ec-tier-level">Level {i + 1}{p.highlight ? " · Most Common" : ""}</div>
              <h3 className="ec-h3">{p.tier}</h3>
              <p>{p.desc}</p>
            </div>
          ))}
        </div>
        <h3 className="ec-subhead">Each package can combine</h3>
        <div className="ec-chips ec-chips-center">
          {packageComponents.map((c, i) => <span key={i} className="ec-chip">{c}</span>)}
        </div>
      </Section>

      {/* ── 05 Ecosystem Digital Tools ── */}
      <Section tone="tint">
        <SectionHead
          n="05" label="Ecosystem Digital Tools"
          title={<>Digital Capabilities <span className="ec-accent-text">for the Ecosystem Network</span></>}
          sub="Ecosystem Digital Tools are participant-facing capabilities delivered through the Ecosystem Network. Ecosystem Packages determine the level of capability available — Digital Tools provide the capability itself."
        />
        <div className="ec-grid">
          {digitalToolCategories.map((cat, i) => (
            <div key={i} className={`ec-card ec-span-${spanOf(digitalToolCategories.length, i)}`}>
              <div className="ec-card-icon" aria-hidden="true">{cat.icon}</div>
              <h3 className="ec-h3">{cat.title}</h3>
              <ul className="ec-list">
                {cat.items.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 06 Ecosystem Intelligence ── */}
      <Section tone="white">
        <SectionHead
          n="06" label="Ecosystem Intelligence"
          title={<>Intelligence — <span className="ec-accent-text">Across the Entire Ecosystem Network</span></>}
          sub="Ecosystem Intelligence operates across the entire architecture — connecting data, context, AI, decisions and actions."
        />
        <div className="ec-grid">
          {intelligenceLayers.map((l, i) => (
            <div key={i} className={`ec-card ec-span-${spanOf(intelligenceLayers.length, i)}`}>
              <div className="ec-card-icon" aria-hidden="true">{l.icon}</div>
              <h3 className="ec-h3">{l.title}</h3>
              <p>{l.desc}</p>
            </div>
          ))}
        </div>
        <h3 className="ec-subhead">How intelligence flows</h3>
        <WordChain words={["Data", "Context", "Intelligence", "Action"]} />
      </Section>

      {/* ── 07 Ecosystem Infrastructure ── */}
      <Section tone="tint">
        <SectionHead
          n="07" label="Ecosystem Infrastructure"
          title={<>The Infrastructure <span className="ec-accent-text">Behind the Ecosystem Network</span></>}
          sub="GBR Farming combines the digital, data, ecosystem edge, integrations and physical infrastructure required for distributed ecosystem operations."
        />
        <div className="ec-grid">
          {infrastructureStacks.map((s, i) => (
            <div key={i} className={`ec-card ec-span-${spanOf(infrastructureStacks.length, i, 6)}`}>
              <div className="ec-card-icon" aria-hidden="true">{s.icon}</div>
              <h3 className="ec-h3">{s.title}</h3>
              <ul className="ec-list ec-list-2col">
                {s.items.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 08 Sovereign & Hybrid Architecture ── */}
      <Section tone="white">
        <SectionHead
          n="08" label="Sovereign & Hybrid Architecture"
          title={<>Shared Platform. <span className="ec-accent-text">Dedicated Sovereign Infrastructure.</span></>}
          sub="GBR Farming combines shared ecosystem services with a dedicated sovereign environment for each ENO — shared where scale matters, dedicated where sovereignty matters, distributed where operations matter."
        />
        <div className="ec-grid">
          {sovereignModel.map((m, i) => (
            <div key={i} className={`ec-card ec-span-${spanOf(sovereignModel.length, i)}`}>
              <div className="ec-card-icon" aria-hidden="true">{m.icon}</div>
              <h3 className="ec-h3">{m.title}</h3>
              <p>{m.desc}</p>
            </div>
          ))}
        </div>
        <div className="ec-callout">
          <div className="ec-eyebrow">Flexible Deployment</div>
          <div className="ec-chips">
            {deploymentOptions.map((d, i) => <span key={i} className="ec-chip ec-chip-white">{d}</span>)}
          </div>
        </div>
      </Section>

      {/* ── 09 Micro-Ecosystem Infrastructure ── */}
      <Section tone="tint">
        <SectionHead
          n="09" label="Micro-Ecosystem Infrastructure"
          title={<>Operate Ecosystems <span className="ec-accent-text">at the Last Mile</span></>}
          sub="Ecosystems ultimately operate through farms, factories, warehouses, processing sites, assets, teams and local operators. GBR Farming enables ENOs to manage these distributed environments as Micro-Ecosystems."
        />
        <div className="ec-chips">
          {microEcosystemAssets.map((a, i) => <span key={i} className="ec-chip ec-chip-white">{a}</span>)}
        </div>
       {/*  <h3 className="ec-subhead">Micro-Ecosystem Networks</h3>
        <div className="ec-grid">
          {microNetworks.map((n, i) => (
            <div key={i} className={`ec-card ec-code-card ec-span-${spanOf(microNetworks.length, i)}`}>
              <span className="ec-code-badge ec-code-badge-alt">{n.code}</span>
              <span className="ec-code-name">{n.name}</span>
            </div>
          ))}
        </div> */}
      </Section>

      {/* ── 10 Integration & Interoperability ── */}
      <Section tone="white">
        <SectionHead
          n="10" label="Integration & Interoperability Architecture"
          title={<>Connect the Ecosystem Network <span className="ec-accent-text">Without Replacing Everything</span></>}
          sub="GBR Farming is designed to connect existing enterprise systems, operational technologies, devices, data sources and ecosystem services — rather than requiring organisations to replace them."
        />
        <Chain steps={integrationFlow} numbered />
        <div className="ec-grid ec-gap-top">
          <div className="ec-card ec-span-6 ec-card-not">
            <div className="ec-eyebrow">What GBR does not do</div>
            <h3 className="ec-h3">Replace existing systems</h3>
            <p>GBR Farming does not require organisations to replace their ERP, CRM, MES or WMS/TMS investments.</p>
          </div>
          <div className="ec-card ec-span-6 ec-card-does">
            <div className="ec-eyebrow">What GBR does instead</div>
            <h3 className="ec-h3">Connect and orchestrate them</h3>
            <p>Existing systems and physical assets are connected and orchestrated within the ecosystem, elevated by sovereign data, edge AI and agentic workflows.</p>
          </div>
        </div>
      </Section>

      {/* ── 11 Security, Trust & Governance ── */}
      <Section tone="tint">
        <SectionHead
          n="11" label="Security, Trust & Governance"
          title={<>Security, Trust &amp; Governance <span className="ec-accent-text">by Design</span></>}
          sub="The ecosystem infrastructure provides foundational capabilities that operate across the entire ecosystem architecture."
        />
        <div className="ec-grid">
          {governanceCapabilities.map((g, i) => (
            <div key={i} className={`ec-card ec-span-${spanOf(governanceCapabilities.length, i)}`}>
              <div className="ec-card-icon" aria-hidden="true">{g.icon}</div>
              <h3 className="ec-h3">{g.title}</h3>
              <ul className="ec-list">
                {g.items.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 12 Infrastructure Differentiation ── */}
      <Section tone="white">
        <SectionHead
          n="12" label="Infrastructure Differentiation"
          title={<>More Than a <span className="ec-accent-text">Software Platform</span></>}
          sub="Traditional software helps an organisation run its business. GBR Farming provides the infrastructure to build, operate and govern an ecosystem."
        />
        <Chain steps={maturitySteps} />

        <h3 className="ec-subhead">Not SaaS. Not ERP. Not MES. Not a Marketplace.</h3>
        <div className="ec-grid">
          {differentiators.map((d, i) => (
            <div key={i} className={`ec-card ec-span-${spanOf(differentiators.length, i)}`}>
              <div className="ec-eyebrow">vs. {d.vs}</div>
              <div className="ec-card-icon" aria-hidden="true">{d.icon}</div>
              <h3 className="ec-h3">{d.title}</h3>
              <p>{d.desc}</p>
            </div>
          ))}
        </div>

        <h3 className="ec-subhead">The GBR Difference</h3>
        <div className="ec-diff-table" role="table">
          <div className="ec-diff-head" role="row">
            <div role="columnheader">Traditional Software</div>
            <div role="columnheader">GBR Farming</div>
          </div>
          {differenceTable.map((row, i) => (
            <div key={i} className="ec-diff-row" role="row">
              <div role="cell">{row[0]}</div>
              <div role="cell">{row[1]}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 13 Operating Model ── */}
      <Section tone="tint">
        <SectionHead
          n="13" label="Operating Model"
          title={<>GBR Farming Provides the Infrastructure. <span className="ec-accent-text">The ENO Owns and Operates the Ecosystem Network.</span></>}
        />
        <div className="ec-grid">
          <div className="ec-card ec-span-6">
            <div className="ec-card-icon" aria-hidden="true"></div>
            <h3 className="ec-h3">GBR Farming Provides</h3>
            <p className="ec-card-note">The infrastructure, platform and capability stack.</p>
            <div className="ec-chips">
              {gbrProvides.map((c, i) => <span key={i} className="ec-chip">{c}</span>)}
            </div>
          </div>
          <div className="ec-card ec-span-6 ec-card-does">
            <div className="ec-card-icon" aria-hidden="true"></div>
            <h3 className="ec-h3">ENO Owns &amp; Operates</h3>
            <p className="ec-card-note">The actual ecosystem, its network and its growth.</p>
            <div className="ec-chips">
              {enoOwns.map((c, i) => <span key={i} className="ec-chip">{c}</span>)}
            </div>
          </div>
        </div>
      </Section>

      {/* ── 14 From Business to Ecosystem ── */}
     {/*  <Section tone="white">
        <SectionHead
          n="14" label="From Business to Ecosystem"
          title={<>From Running a Business to <span className="ec-accent-text">Operating an Ecosystem</span></>}
        />
        <div className="ec-grid">
          <div className="ec-card ec-span-12">
            <div className="ec-eyebrow">Traditional Business</div>
            <WordChain words={traditionalBusiness} />
          </div>
          <div className="ec-card ec-span-12 ec-card-does">
            <div className="ec-eyebrow">Ecosystem Business</div>
            <WordChain words={ecosystemBusiness} />
          </div>
        </div>
      </Section> */}

      {/* ── Closing CTA (breaks the pattern for emphasis) ── */}
      <section className="ec-section ec-tone-accent">
        <Reveal>
          <div className="ec-container ec-closing">
            <h2 className="ec-h2">
              Build the Ecosystem. Own the Infrastructure.<br />
              <span className="ec-accent-text">Operate the Network.</span>
            </h2>
            <p className="ec-lead">GBR Farming provides the infrastructure. You operate the ecosystem.</p>
            <a href="/book-demo" className="ec-btn ec-btn-light">Request a Demo →</a>
          </div>
        </Reveal>
      </section>

    </div>
  );
};

export default PlatformSoon;
